// variant of src/components/pages/region-details/components/all-productions-block/components/production-listings/component.tsx

import React, { useContext, useRef, useState } from 'react'

import { Box, Button, Column, Grid, ListingSkeletonLoader, Row, Text } from '@vividseats/vivid-ui-kit'
import dayjs from 'dayjs'

import OverFiltered from '@/components/shared/over-filtered'
import ProductionListing from '@/components/shared/production-listing'
import { CONCERTS_SUB_CATEGORY_IDS, VISIBLE_BY_DEFAULT_PRODUCTION_LISTINGS_LIMIT } from '@/constants'
import ProductionListContext from '@/context/productions-list'
import ProductionsRowsContext from '@/context/productions-rows'
import { useTrackViewProductionList } from '@/hooks/use-analytics'
import { Production, ProductionListSelectorType, ProductionListSelectorWithRowsInfoType } from '@/types/app-types'
import { formatDateRange } from '@/utils/dates'

import NoNearbyListings from './components/no-nearby-listings/component'
import ProductionListingMobile from './components/production-listing-mobile'
import useUpdateFormattedProductionProperties from './hooks/update-formatted-production-properties/hook'
import useUpdateProductionProperties from './hooks/update-production-properties/hook'
import useSaveScrollPosition from './hooks/use-save-scroll-position/hook'
import useScrollOnListCollapse from './hooks/use-scroll-on-list-collapse/hook'
import styles from './styles.module.scss'

// if a date range exists within stringWithRange that has a start date that matches shortLocalDate, formatStringDateRange returns the date range as a formatted string
export const formatStringDateRange = (stringWithRange: string, shortLocalDate: string): string | null => {
    const dateRangeMatch = stringWithRange.match(/\d+\/\d+ - \d+\/\d+/)

    if (!dateRangeMatch) {
        return null
    }

    const [stringStartDate, stringEndDate] = dateRangeMatch[0].split(/ - /)
    const date = dayjs(shortLocalDate)
    const year = date.year()
    const startDate = dayjs(`${stringStartDate}/${year}`)
    // check that dates in range match shortLocalDate
    if (date.isSame(startDate)) {
        const endDate = dayjs(`${stringEndDate}/${year}`)
        return formatDateRange(startDate, endDate)
    } else return null
}

interface Props {
    title?: string
    withDivider?: boolean
    customOnFilterResetAction?: () => void
    subCategoryId?: number
    categoryId?: number // TODO: clean up unused categoryId
    noSearchResultsMessage?: React.ReactNode
    showMoreButtonEnabled?: boolean
    onProductionClickCapture?: (production: Production, index: number) => void
    customSort?: <T extends ProductionListSelectorType[] | ProductionListSelectorWithRowsInfoType[]>(a: T) => T
}

const ProductionListings: React.FC<Props> = ({
    title,
    withDivider = true,
    customOnFilterResetAction,
    subCategoryId,
    noSearchResultsMessage,
    showMoreButtonEnabled = true,
    onProductionClickCapture,
    customSort,
}) => {
    const {
        selectors: { analyticsSubIds },
    } = useContext(ProductionListContext)

    const {
        state: { fetching },
        selectors: {
            productions,
            visibleByDefaultProductions,
            hiddenByDefaultProductions,
            visibleByDefaultProductionsWithFormat,
            hiddenByDefaultProductionsWithFormat,
            showProductionsData,
            hasFiltersApplied,
        },
    } = useContext(ProductionsRowsContext)

    const { isListExpandedLocalStorage, setIsListExpandedLocalStorage } = useSaveScrollPosition()

    const [isShowingMoreProductions, setIsShowingMoreProductions] = useState<boolean>(
        isListExpandedLocalStorage ?? false,
    )

    const buttonRef = useRef<HTMLButtonElement>(null)
    const listRef = useRef<HTMLDivElement>(null)

    useScrollOnListCollapse(isShowingMoreProductions, buttonRef, listRef)

    useTrackViewProductionList(!fetching, productions, ...analyticsSubIds)

    const isFestival = subCategoryId === CONCERTS_SUB_CATEGORY_IDS.FESTIVALS

    const isCustomPage = (() => {
        if (typeof window !== 'undefined') return window.location.href.includes('/custom/')
        return false
    })()

    const visibleProductions = isShowingMoreProductions
        ? [...visibleByDefaultProductions, ...hiddenByDefaultProductions]
        : visibleByDefaultProductions

    const productionsSorted = useUpdateProductionProperties(
        visibleProductions,
        subCategoryId,
        onProductionClickCapture,
        customSort,
    )

    const visibleFormattedProductions = isShowingMoreProductions
        ? [...visibleByDefaultProductionsWithFormat, ...hiddenByDefaultProductionsWithFormat]
        : visibleByDefaultProductionsWithFormat

    const productionsFormattedSorted = useUpdateFormattedProductionProperties(
        visibleFormattedProductions,
        subCategoryId,
        onProductionClickCapture,
        customSort,
    )

    if (!hasFiltersApplied && productions.length === 0 && noSearchResultsMessage) {
        return <div>{noSearchResultsMessage}</div>
    }

    const buttonLabel = isShowingMoreProductions ? 'Show Less' : 'Show More'

    const onShowMoreClick = () => {
        setIsListExpandedLocalStorage(!isShowingMoreProductions)
        setIsShowingMoreProductions(!isShowingMoreProductions)
    }

    return (
        <div>
            {title && (
                <Grid>
                    <Row className={styles.row} align="end">
                        <Column className={styles.title} pl={isFestival ? 0.5 : 1}>
                            <Text as="h2" size="xl" weight="bold">
                                {title}
                            </Text>
                        </Column>
                    </Row>
                </Grid>
            )}

            <ListingSkeletonLoader
                isLoading={Boolean(fetching) && !showProductionsData}
                rowCount={VISIBLE_BY_DEFAULT_PRODUCTION_LISTINGS_LIMIT}
            >
                {showProductionsData ? (
                    <Box marginBottom={withDivider ? 0 : 7}>
                        <div ref={listRef}>
                            {isFestival ? (
                                <>
                                    {productionsSorted.map((production) => (
                                        <ProductionListingMobile key={production.id} {...production} />
                                    ))}
                                </>
                            ) : (
                                <>
                                    <Box display="none" tabletDisplay="block" data-testid="desktop-list">
                                        {productionsFormattedSorted.map((production) => (
                                            <ProductionListing key={production.id} {...production} />
                                        ))}
                                    </Box>
                                    <Box tabletDisplay="none" data-testid="mobile-list">
                                        {productionsSorted.map((production) => (
                                            <ProductionListingMobile key={production.id} {...production} />
                                        ))}
                                    </Box>
                                </>
                            )}
                        </div>

                        {showMoreButtonEnabled &&
                        // Don't show the button if there's no extra productions
                        (hiddenByDefaultProductions.length || hiddenByDefaultProductionsWithFormat.length) ? (
                            <div className={styles.buttonWrapper}>
                                <Button
                                    ref={buttonRef}
                                    data-testid="show-more-btn"
                                    disabled={fetching}
                                    size="large"
                                    importance="tertiary"
                                    onClick={onShowMoreClick}
                                >
                                    <Text weight="regular">{buttonLabel}</Text>
                                </Button>
                            </div>
                        ) : null}

                        {withDivider && (
                            <Column mt={2} mb={2}>
                                <hr />
                            </Column>
                        )}
                    </Box>
                ) : hasFiltersApplied ? (
                    <OverFiltered
                        customOnFilterResetAction={customOnFilterResetAction}
                        withDivider={withDivider}
                        withBottomPadding={!withDivider}
                    />
                ) : (
                    isCustomPage && <NoNearbyListings withDivider={withDivider} />
                )}
            </ListingSkeletonLoader>
        </div>
    )
}

export default ProductionListings
